import React, { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  Globe,
  Smartphone,
  Sparkles,
  Shield,
  Brain,
  Layers,
} from "lucide-react";

// Interactive Reveal Hook
function useReveal() {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// Expanded Portfolio Data to match your tabs
const portfolioData = [
  {
    id: "visa",
    tabTitle: "Travel Technology",
    website: "https://docudost.com",
    title: "Visa Documentation Portal",
    icon: Globe,
    image: "/docudost.png",
    description: "A secure visa application platform with real-time tracking, cloud document upload, automated compliance checks, and user authentication management.",
    features: ["AWS & Cloud Services", "DevOps Infrastructure", "Secure File Encryption", "AI Automation", "Intelligent Dashboards", "API Integration"],
    status: "Client Project",
  }
];

export default function Portfolio() {
  const [activeTab, setActiveTab] = useState("visa");
  const headerRef = useReveal();
  const showcaseRef = useReveal();

  // Find currently selected project details
  const activeProject = portfolioData.find((p) => p.id === activeTab) || portfolioData[0];
  const ProjectIcon = activeProject.icon;


  return (
    // <section className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
      // Change "py-20 lg:py-28" to explicit top (pt) and bottom (pb) controls
<section id="portfolio" className="relative overflow-hidden bg-[#FAF7F2] pt-8 lg:pt-0 pb-20 lg:pb-18 scroll-mt-24">
      {/* Soft Background Accent Orbs */}
      <div className="absolute left-[-10%] top-0 h-[600px] w-[600px] rounded-full bg-[#C8762A]/5 blur-[150px] pointer-events-none" />
      <div className="absolute right-[-10%] bottom-0 h-[600px] w-[600px] rounded-full bg-[#C8762A]/5 blur-[150px] pointer-events-none" />

      {/* Main Structural Container */}
      <div className="relative mx-auto w-[90%] max-w-6xl xl:max-w-7xl">
        
        {/* Header Section from Image Design */}
        <div ref={headerRef} className="translate-y-8 opacity-0 transition-all duration-700 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.3em] text-[#C87-[#C8762A]">
            <Sparkles size={12} className="fill-[#C8762A]/20" />
            ZevixTech Solutions
          </span>

          <h2 className="mx-auto mt-4 max-w-4xl text-3xl font-extrabold tracking-tight text-[#1A1714] sm:text-4xl lg:text-5xl leading-[1.15]">
          Innovating Technology That Drives{" "}
          <span className="text-[#C8762A]">Business Growth</span>
        </h2>
        </div>

        {/* Dynamic Category Navigation Bar (Pill Bar) */}
        <div className="mt-12 flex justify-center">
          <div className="inline-flex w-full max-w-full items-center gap-1 overflow-x-auto rounded-full bg-[#1A1714] p-1.5 shadow-xl shadow-stone-900/10 no-scrollbar md:w-auto">
            {portfolioData.map((project) => (
              <button
                key={project.id}
                onClick={() => setActiveTab(project.id)}
                className={`whitespace-nowrap rounded-full px-6 py-3 text-xs font-semibold tracking-wide transition-all duration-300 ${
                  activeTab === project.id
                    ? "bg-[#C8762A] text-white shadow-md shadow-[#C8762A]/30"
                    : "text-stone-400 hover:text-white"
                }`}
              >
                {project.tabTitle}
              </button>
            ))}
          </div>
        </div>

        {/* Main Showcase Split Grid */}
        <div
          ref={showcaseRef}
          key={activeTab} // Forces smooth transition wrapper on state shift
          className="mt-16 grid items-center gap-10 md:gap-14 lg:grid-cols-12 animate-fadeIn"
        >
          {/* Left Column: Visual Image Block */}
          <div className="group relative overflow-hidden rounded-[32px] lg:col-span-6 shadow-lg shadow-stone-900/5">
            <img
              src={activeProject.image}
              alt={activeProject.title}
              className="h-[300px] sm:h-[380px] lg:h-[460px] xl:h-[490px] w-full object-cover transition duration-700 group-hover:scale-103"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/60 via-transparent to-transparent" />
            <div className="absolute left-6 bottom-6">
              <span className="rounded-full bg-white/95 backdrop-blur-sm px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#C8762A] shadow-sm">
                {activeProject.status}
              </span>
            </div>
          </div>

          {/* Right Column: Detailed Content Area */}
          <div className="lg:col-span-6">
            <h3 className="text-3xl font-bold tracking-tight text-[#1A1714] sm:text-4xl">
              {activeProject.title}
            </h3>

            <p className="mt-4 text-base leading-7 text-stone-600 md:text-md md:leading-8">
              {activeProject.description}
            </p>

            {/* Custom 2-Column Balanced Feature Grid */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5 border-t border-[#E6D8C5]/60 pt-6">
              {activeProject.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="h-2 w-2 rounded-full bg-[#C8762A]" />
                  <span className="text-sm font-medium text-[#3D3830]">
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            {/* Premium Capsule Button Styling */}
            <div className="mt-10">
              <button
                onClick={() => window.open(activeProject.website, "_blank")}
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#C8762A] px-7 py-3.5 font-bold text-white transition-all duration-300 hover:bg-[#b0631f] hover:shadow-lg hover:shadow-[#C8762A]/20 text-sm"
              >
                View Project
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
                </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}